// Hit testing (§4.10): layout coordinates → beat / lane / string / staff position.

import type { Score, BeatRef } from '@simplenotation/core';
import { voiceOf, filledTicks, barCapacityAt } from '@simplenotation/core';
import type { Layout, Band, BarBox, SystemBox, BeatBox } from './types';

export interface Hit {
  ref: BeatRef;
  lane: 'staff' | 'tab';
  string: number;
  staffPos: number;
  band: Band;
  system: SystemBox;
}

export function systemAt(layout: Layout, y: number): SystemBox | null {
  let best: SystemBox | null = null;
  let bestD = Infinity;
  for (const s of layout.systems) {
    const d = y < s.y0 ? s.y0 - y : y > s.y1 ? y - s.y1 : 0;
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  return best;
}

export function bandAt(sys: SystemBox, y: number, sp: number): Band | null {
  let best: Band | null = null;
  let bestD = Infinity;
  for (const b of sys.bands) {
    const top = b.top - (b.kind === 'staff' ? 3 * sp : 1.2 * sp);
    const bottom = b.bottom + (b.kind === 'staff' ? 3 * sp : 1.2 * sp);
    const d = y < top ? top - y : y > bottom ? y - bottom : 0;
    const center = Math.abs(y - (b.top + b.bottom) / 2) / 1000;
    if (d + center < bestD) {
      bestD = d + center;
      best = b;
    }
  }
  return best;
}

export function barBoxAt(layout: Layout, sys: SystemBox, x: number): BarBox | null {
  const boxes = layout.bars.filter((b) => b.system === sys.index);
  if (!boxes.length) return null;
  for (const b of boxes) if (x >= b.x0 && x < b.x1) return b;
  return x < boxes[0].x0 ? boxes[0] : boxes[boxes.length - 1];
}

export function hitTest(layout: Layout, score: Score, x: number, y: number, voice: number): Hit | null {
  const sys = systemAt(layout, y);
  if (!sys) return null;
  const band = bandAt(sys, y, layout.sp);
  if (!band) return null;
  const bb = barBoxAt(layout, sys, x);
  if (!bb) return null;
  const track = band.track;
  const beats = voiceOf(score, { track, bar: bb.bar, voice });
  let beat = 0;
  if (beats.length === 0) beat = 0;
  else {
    // Nearest beat by notehead centre.
    let bestD = Infinity;
    beats.forEach((b, i) => {
      const box = layout.beatIndex.get(b.id);
      if (!box) return;
      const cx = box.x + box.w / 2;
      const d = Math.abs(cx - x);
      if (d < bestD) {
        bestD = d;
        beat = i;
      }
    });
    // Clicking well past the last beat of an incomplete bar targets the insertion point.
  }
  const sp = layout.sp;
  let string = 1;
  let staffPos = 0;
  if (band.kind === 'tab') {
    string = Math.max(1, Math.min(band.lines, Math.round((y - band.top) / band.gap) + 1));
  } else {
    staffPos = Math.round((2 * sp - (y - band.top)) / (sp / 2));
    staffPos = Math.max(-16, Math.min(16, staffPos));
  }
  return { ref: { track, bar: bb.bar, voice, beat }, lane: band.kind, string, staffPos, band, system: sys };
}

export interface DropHit extends Hit {
  /** True when the drop goes after the last beat of a bar (`ref.beat` = voice length). */
  insert: boolean;
  /** True when that bar is the full last bar: the drop adds a new bar after it. */
  append?: boolean;
  /** Centre x of the column the note lands in. */
  cx: number;
}

/**
 * Where a note dragged from the palette lands: the nearest beat, or the bar's
 * insertion point when the pointer is right of the last beat and the bar is
 * not full yet. Right of the last beat of a full last bar, the note goes into
 * a new bar after it.
 */
export function dropTarget(layout: Layout, score: Score, x: number, y: number, voice: number): DropHit | null {
  const hit = hitTest(layout, score, x, y, voice);
  if (!hit) return null;
  const bar = layout.bars.find((b) => b.bar === hit.ref.bar && b.system === hit.system.index);
  if (!bar) return null;
  const sp = layout.sp;
  const beats = voiceOf(score, hit.ref);
  if (beats.length === 0) return { ...hit, insert: true, cx: (bar.columns[0]?.x ?? bar.contentX) + 0.6 * sp };
  const last = layout.beatIndex.get(beats[beats.length - 1].id);
  const room = filledTicks(beats) < barCapacityAt(score, hit.ref.bar);
  if (last && x > last.x + last.w + 0.6 * sp) {
    const at = { ...hit.ref, beat: beats.length };
    if (room) return { ...hit, ref: at, insert: true, cx: Math.min(bar.x1 - 0.7 * sp, last.x + last.w + 1.6 * sp) };
    if (hit.ref.bar === score.bars.length - 1) return { ...hit, ref: at, insert: true, append: true, cx: bar.x1 + 2 * sp };
  }
  const box = layout.beatIndex.get(beats[hit.ref.beat]?.id ?? '');
  return { ...hit, insert: false, cx: box ? box.x + box.w / 2 : x };
}

/** BeatBox of a ref (or a virtual one for an insertion point). */
export function boxForRef(layout: Layout, score: Score, ref: BeatRef): BeatBox | null {
  const beats = voiceOf(score, ref);
  const b = beats[ref.beat];
  if (b) return layout.beatIndex.get(b.id) ?? null;
  // Insertion point: after the last beat, or at the bar start.
  const bar = layout.bars.find((x) => x.bar === ref.bar);
  if (!bar) return null;
  const last = beats.length ? layout.beatIndex.get(beats[beats.length - 1].id) : null;
  const x = last ? last.xEnd : bar.columns[0]?.x ?? bar.contentX;
  return { ref, beatId: '', system: bar.system, x, w: layout.sp * 1.18, xEnd: x + layout.sp * 2, tick: 0, rest: true, noteYs: [], tabYs: [] };
}

/** x position of a tick within a bar (playhead interpolation). */
export function xAtTick(bar: BarBox, tickInBar: number): number {
  const cols = bar.columns;
  if (!cols.length) return bar.x0;
  if (tickInBar <= cols[0].tick) return cols[0].x;
  for (let i = 0; i < cols.length; i++) {
    const a = cols[i];
    const nextTick = i + 1 < cols.length ? cols[i + 1].tick : bar.length;
    const nextX = i + 1 < cols.length ? cols[i + 1].x : bar.x1 - 0.2;
    if (tickInBar < nextTick) {
      const f = (tickInBar - a.tick) / Math.max(1, nextTick - a.tick);
      return a.x + f * (nextX - a.x);
    }
  }
  return bar.x1;
}
